<script setup>
import { computed, defineProps } from 'vue'

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
})

// CE flexform settings
const settings = computed(() => props.card?.settings ?? {})

// helper: first media entry by field name
const mediaByField = (field) => {
  const list = Array.isArray(props.card?.media) ? props.card.media : []
  const key = String(field || '').toLowerCase()
  return list.find(m => (m?.field || '').toLowerCase() === key) || null
}

const videoFile = computed(() => mediaByField('video_file'))
const previewImage = computed(() => mediaByField('preview_image'))

const wrapperClasses = computed(() => {
  const cls = ['content-item', 'module-video', '-full-size']

  if (settings.value.hideForABTest) cls.push('-hide-ab-test')
  if (settings.value.hideMobile) cls.push('-hide-mobile')
  if (settings.value.hideTablet) cls.push('-hide-tablet')
  if (settings.value.hideDesktop) cls.push('-hide-desktop')

  return cls.join(' ')
})

// Build the YouTube embed URL from card.video (ID or code)
const youtubeSrc = computed(() => {
  const v = (props.card?.video || '').trim()
  if (!v) return ''
  // mimic Fluid: https://www.youtube-nocookie.com/embed/{item.video}?...
  return `https://www.youtube-nocookie.com/embed/${v}?rel=0&showinfo=0&enablejsapi=1`
})

// Fallback preview URL (if no previewImage from FAL)
const fallbackPreviewUrl = computed(() => {
  if (props.card?.url) return props.card.url
  if (!props.card?.video) return ''
  return `https://i.ytimg.com/vi/${props.card.video}/mqdefault.jpg`
})
</script>

<template>
  <div
    :class="wrapperClasses"
    :id="settings.anchor || null"
  >
    <div class="cards__card">
      <div class="card__video">
        <template v-if="videoFile">
          <video controls>
            <source
              :src="videoFile.url"
              type="video/mp4"
            />
          </video>
        </template>
        <template v-else-if="youtubeSrc">
          <iframe
            :src="youtubeSrc"
            frameborder="0"
            allowfullscreen
          ></iframe>
        </template>
      </div>
      <div class="card__iframe">
        <template v-if="previewImage">
          <img
            :src="previewImage.url"
            loading="lazy"
            :alt="previewImage.alt || card.preview_img_alt || ''"
            :title="previewImage.title || ''"
          />
        </template>
        <template v-else-if="fallbackPreviewUrl">
          <img
            :src="fallbackPreviewUrl"
            loading="lazy"
            :alt="card.preview_img_alt || ''"
          />
        </template>

        <div class="uc-placeholder"></div>
      </div>
      <div
        v-if="card.title"
        class="card__caption"
      >
        {{ card.title }}
      </div>
    </div>
  </div>
</template>
